// eslint-disable-next-line no-restricted-imports
import { useFlag, useFlags, useVariant } from '@unleash/proxy-client-react';
import { isValid } from 'date-fns';
import { useContext, useMemo } from 'react';

import { FeatureFlag } from '../flags';
import { UnleashFallbackContext } from './unleash-fallback.context';

export const useUnleashFlag = (flag: FeatureFlag) => {
  const unleashClientFlagValue = useFlag(flag);
  const flagContext = useContext(UnleashFallbackContext);
  const flags = flagContext?.flags;
  const usingFallback = flagContext?.usingFallback;

  if (usingFallback) {
    return flags[flag] ?? false;
  }
  return unleashClientFlagValue;
};

export const useUnleashFlags = (): Record<FeatureFlag, boolean> => {
  const unleashClientFlags = useFlags();
  const { flags: fallbackFlags, usingFallback } = useContext(
    UnleashFallbackContext
  );

  return useMemo(() => {
    const defaults = Object.fromEntries(
      (Object.values(FeatureFlag) as FeatureFlag[]).map((f) => [f, false])
    ) as Record<FeatureFlag, boolean>;

    if (usingFallback) {
      return { ...defaults, ...(fallbackFlags ?? {}) };
    }

    const clientFlags: Partial<Record<FeatureFlag, boolean>> = {};
    unleashClientFlags.forEach((flag) => {
      clientFlags[flag.name as FeatureFlag] = flag.enabled;
    });
    return { ...defaults, ...clientFlags };
  }, [fallbackFlags, usingFallback, unleashClientFlags]);
};

const sessionStartTimestamp = new Date();

export const useUnleashVersionRefresh = () => {
  const refreshVariant = useVariant(FeatureFlag.Refresh);

  const refreshTimestamp =
    refreshVariant?.name === 'refresh-timestamp'
      ? refreshVariant?.payload?.value
      : null;

  const shouldRefresh = (() => {
    if (!refreshTimestamp) return false;

    const refreshDate = new Date(refreshTimestamp);

    if (
      sessionStartTimestamp &&
      refreshDate &&
      isValid(refreshDate) &&
      refreshDate < new Date() &&
      refreshDate > sessionStartTimestamp
    ) {
      return true;
    }

    return false;
  })();

  if (shouldRefresh) {
    // Remove query params and reload the page
    const { origin, pathname } = window.location;
    window.location.replace(`${origin}${pathname}`);
  }
};
